import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader, Search } from 'lucide-react';
import Select from 'react-select';
import { buildSerpUrl } from '../lib/serpUrl';
import { checkIsReactSnap } from '../hooks/useIsReactSnap';
import { LANGUAGES, COUNTRIES, flagEmoji } from '../data/serpOptions';
import LocationSelect from './LocationSelect';
import SerpResultPanel from './SerpResultPanel';

// react-select skin matching the reference's 48px / 4px-radius controls.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 48,
    borderRadius: 4,
    borderColor: state.isFocused ? '#175ce5' : '#dadee5',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(23, 92, 229, 0.1)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#175ce5' : '#b6bfcc' },
    fontSize: 15,
  }),
  valueContainer: (base) => ({ ...base, padding: '2px 8px 2px 12px' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, color: '#94a3b8', paddingRight: 10 }),
  menu: (base) => ({ ...base, zIndex: 50, borderRadius: 4 }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#175ce5' : state.isFocused ? '#eef3fe' : '#ffffff',
    color: state.isSelected ? '#ffffff' : '#1f2937',
  }),
};

const selectTheme = (theme) => ({
  ...theme,
  colors: { ...theme.colors, primary: '#175ce5', primary25: '#eef3fe', primary50: '#dbe6fc' },
});

// Flag + name for countries; name + dimmed hl badge for languages (the badge
// inherits the option's text color so it stays readable on the selected blue).
const formatCountryOption = (option) => (
  <span className="flex items-center gap-2">
    <span aria-hidden="true">{flagEmoji(option.value)}</span>
    <span className="truncate">{option.label}</span>
  </span>
);

const formatLanguageOption = (option) => (
  <span className="flex w-full items-center justify-between gap-2">
    <span className="truncate">{option.label}</span>
    <span className="text-[11px] font-medium uppercase tracking-wide opacity-60">
      {option.value}
    </span>
  </span>
);

const SerpChecker = ({ onShare }) => {
  const [formData, setFormData] = useState({
    keyword: '',
    hostLanguage: 'en',
    geoLocation: 'US',
    city: '',
    resultsPerPage: 10,
    safeSearch: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Last check, rendered as the results panel: { keyword, countryLabel,
  // language, city, url, status: 'opened'|'blocked', note, at }
  const [result, setResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);
  const justSelectedSuggestion = useRef(false);
  const prefsLoaded = useRef(false);

  // Restore saved preferences (language / country / city) AFTER mount — in an
  // effect, not initial state, so the prerendered HTML and the first client
  // render match (no hydration mismatch).
  useEffect(() => {
    if (checkIsReactSnap()) return;
    try {
      const saved = JSON.parse(localStorage.getItem('fsc-prefs') || '{}');
      setFormData((prev) => ({
        ...prev,
        hostLanguage: saved.hostLanguage || prev.hostLanguage,
        geoLocation: saved.geoLocation || prev.geoLocation,
        city: typeof saved.city === 'string' ? saved.city : prev.city,
      }));
    } catch (e) {
      /* ignore corrupt/unavailable storage */
    }
  }, []);

  // Persist language / country / city so they survive reloads. Skips the first
  // run so the initial defaults never overwrite what was just restored above.
  useEffect(() => {
    if (checkIsReactSnap()) return;
    if (!prefsLoaded.current) {
      prefsLoaded.current = true;
      return;
    }
    try {
      localStorage.setItem(
        'fsc-prefs',
        JSON.stringify({
          hostLanguage: formData.hostLanguage,
          geoLocation: formData.geoLocation,
          city: formData.city,
        })
      );
    } catch (e) {
      /* storage may be unavailable */
    }
  }, [formData.hostLanguage, formData.geoLocation, formData.city]);

  // Google Suggest via JSONP. Memoized so the debounce/update effects can list
  // it as a dependency (fixes the exhaustive-deps warnings that broke CI builds).
  const fetchSuggestions = useCallback(async (query) => {
    if (checkIsReactSnap()) return;

    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const callbackName = 'googleSuggestCallback_' + Math.random().toString(36).substr(2, 9);
      const script = document.createElement('script');
      const params = new URLSearchParams({
        q: query,
        hl: formData.hostLanguage,
        gl: formData.geoLocation.toLowerCase(),
        xhr: 0,
        client: 'psy-ab',
        callback: callbackName
      });
      script.src = `https://www.google.com/complete/search?${params.toString()}`;

      const fetched = await new Promise((resolve, reject) => {
        let timeoutId = null;
        // Idempotent teardown shared by the success / error / timeout paths
        // (the old per-path removeChild could throw on double cleanup).
        const cleanup = () => {
          delete window[callbackName];
          if (timeoutId) clearTimeout(timeoutId);
          if (script.isConnected) script.remove();
        };

        window[callbackName] = (data) => {
          try {
            if (data && data[1]) {
              resolve(
                data[1].map((item) => {
                  const text = typeof item[0] === 'string' ? item[0] : item[0];
                  // Decode HTML entities/tags safely without innerHTML (B5)
                  return new DOMParser().parseFromString(text, 'text/html').body.textContent || text;
                })
              );
            } else {
              resolve([]);
            }
          } catch (err) {
            reject(err);
          } finally {
            cleanup();
          }
        };

        script.onerror = () => {
          cleanup();
          reject(new Error('Failed to load suggestions'));
        };

        timeoutId = setTimeout(() => {
          if (window[callbackName]) {
            cleanup();
            reject(new Error('Request timeout'));
          }
        }, 5000);

        document.head.appendChild(script);
      });

      setSuggestions(fetched);
      setShowSuggestions(fetched.length > 0);
      setSelectedSuggestionIndex(-1);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [formData.hostLanguage, formData.geoLocation]);

  // Latest-ref so the debounce effect below reacts ONLY to keystrokes: with
  // fetchSuggestions itself in its deps, a country/language change would
  // re-run it and reopen suggestions the user already dismissed.
  const fetchSuggestionsRef = useRef(fetchSuggestions);
  fetchSuggestionsRef.current = fetchSuggestions;

  // Debounced suggest on keyword changes
  useEffect(() => {
    if (justSelectedSuggestion.current) {
      justSelectedSuggestion.current = false;
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (formData.keyword.trim().length >= 2) {
        fetchSuggestionsRef.current(formData.keyword);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formData.keyword]);

  // Refresh open suggestions when language/country change
  useEffect(() => {
    if (showSuggestions && formData.keyword.trim().length >= 2) {
      fetchSuggestions(formData.keyword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.hostLanguage, formData.geoLocation, fetchSuggestions]);

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && suggestions[selectedSuggestionIndex]) {
          justSelectedSuggestion.current = true;
          handleInputChange('keyword', suggestions[selectedSuggestionIndex]);
          setShowSuggestions(false);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    justSelectedSuggestion.current = true;
    handleInputChange('keyword', suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  // Geocode a city/address string to coordinates via OpenStreetMap Nominatim.
  // Returns { lat, lng } (strings) or null if it can't be resolved. No state
  // mutation — the caller (handleSubmit) owns loading/errors.
  const geocodeCity = async (city) => {
    const query = (city || '').trim();
    if (!query) return null;

    // Accept a raw "lat, lng" pair directly.
    const coordMatch = query.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat: lat.toString(), lng: lng.toString() };
      }
    }

    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data && data.length > 0) {
        return { lat: data[0].lat.toString(), lng: data[0].lon.toString() };
      }
    } catch (err) {
      console.error('Geocoding error:', err);
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (checkIsReactSnap()) return;

    if (!formData.keyword.trim()) {
      setError('Please enter a keyword');
      return;
    }

    setIsLoading(true);
    setError('');

    // The city path awaits geocoding before the URL exists, and browsers (notably
    // Safari) drop the click's "user activation" across an await — which would make
    // window.open register as a blocked popup even when nothing is wrong. So for the
    // city path we open a blank tab SYNCHRONOUSLY now (inside the gesture) and
    // navigate it once the URL is ready. The country-only path has no await, so it
    // opens the final URL directly (no blank-tab flash).
    const city = formData.city.trim();
    const needsGeocode = !!city;
    let pendingWin = null;
    if (needsGeocode) {
      pendingWin = window.open('', '_blank');
      if (pendingWin) pendingWin.opener = null;
    }

    try {
      // A typed city gives a precise (V2) SERP; country alone gives a V2 SERP
      // pointed at the capital (COUNTRY_COORDS). Geocoding happens on search.
      let coords = null;
      let note = '';
      if (needsGeocode) {
        coords = await geocodeCity(city);
        if (!coords) {
          // Non-blocking: we still open a valid country-level SERP.
          note = "Couldn't pinpoint that city — showing country-level results.";
        }
      }

      const country = COUNTRIES.find((g) => g.value === formData.geoLocation);
      const countryLabel = country ? country.label : formData.geoLocation;
      const searchUrl = buildSerpUrl({
        keyword: formData.keyword,
        countryCode: formData.geoLocation,
        countryLabel,
        countryCanonical: country ? country.canonical : undefined,
        coords,
        language: formData.hostLanguage,
        num: formData.resultsPerPage,
        safe: formData.safeSearch,
      });

      let status;
      if (needsGeocode) {
        // Navigate the tab we pre-opened inside the gesture (or fall back if even
        // that synchronous open was blocked).
        if (pendingWin && !pendingWin.closed) {
          pendingWin.location = searchUrl;
          status = 'opened';
        } else {
          status = 'blocked';
        }
      } else {
        // Country-only: open the final URL directly (no await consumed the gesture).
        // Open WITHOUT 'noopener' so we get a real handle to detect a blocked popup,
        // then sever the reverse reference manually.
        const win = window.open(searchUrl, '_blank');
        if (win) {
          win.opener = null;
          status = 'opened';
        } else {
          status = 'blocked';
        }
      }

      setResult({
        keyword: formData.keyword,
        countryLabel,
        language: formData.hostLanguage,
        city: coords ? city : '',
        note,
        url: searchUrl,
        status,
        at: Date.now(),
      });
    } catch (err) {
      if (pendingWin) pendingWin.close(); // don't leave a blank tab open on error
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedLanguage = LANGUAGES.find(lang => lang.value === formData.hostLanguage) || LANGUAGES[0];
  const selectedCountry = COUNTRIES.find(geo => geo.value === formData.geoLocation) || COUNTRIES[0];
  const canSearch = !isLoading && formData.keyword.trim().length > 0;

  return (
    <div className="serp-checker-form">
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Keyword with Google Suggest */}
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400"
          />
          <input
            ref={inputRef}
            type="text"
            id="keyword"
            aria-label="Search keyword"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showSuggestions && suggestions.length > 0}
            aria-controls={showSuggestions && suggestions.length > 0 ? 'keyword-suggestions' : undefined}
            aria-activedescendant={selectedSuggestionIndex >= 0 ? `suggestion-${selectedSuggestionIndex}` : undefined}
            value={formData.keyword}
            onChange={(e) => handleInputChange('keyword', e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter a keyword…"
            className="serp-input pl-11 pr-3.5"
            required
          />
          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              id="keyword-suggestions"
              role="listbox"
              aria-label="Search suggestions"
              className="absolute z-50 w-full mt-1 bg-white border border-[#dadee5] rounded shadow-lg max-h-60 overflow-y-auto"
            >
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  id={`suggestion-${index}`}
                  role="option"
                  aria-selected={index === selectedSuggestionIndex}
                  className={`px-4 py-2 cursor-pointer hover:bg-slate-100 text-left text-[15px] ${
                    index === selectedSuggestionIndex ? 'bg-brand-50 border-l-4 border-brand-600' : ''
                  }`}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Country / Language / Location, one row like the reference */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Select
            inputId="geoLocation"
            instanceId="geoLocation"
            aria-label="Country"
            value={selectedCountry}
            onChange={(option) => handleInputChange('geoLocation', option.value)}
            options={COUNTRIES}
            formatOptionLabel={formatCountryOption}
            placeholder="Select country"
            className="text-left"
            classNamePrefix="fsc-select"
            styles={selectStyles}
            theme={selectTheme}
            isSearchable
          />
          <Select
            inputId="language"
            instanceId="language"
            aria-label="Host language"
            value={selectedLanguage}
            onChange={(option) => handleInputChange('hostLanguage', option.value)}
            options={LANGUAGES}
            formatOptionLabel={formatLanguageOption}
            placeholder="Select language"
            className="text-left"
            classNamePrefix="fsc-select"
            styles={selectStyles}
            theme={selectTheme}
            isSearchable
          />
          <LocationSelect
            value={formData.city}
            onChange={(value) => handleInputChange('city', value)}
          />
        </div>

        {/* Centered primary action, disabled until there is a keyword */}
        <div className="flex justify-center pt-2">
          <button type="submit" disabled={!canSearch} className="serp-submit">
            {isLoading && <Loader aria-hidden="true" className="w-4 h-4 animate-spin" />}
            Search
          </button>
        </div>

        {/* Hard error (blocking): unexpected failure */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded" role="alert">
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </form>

      <SerpResultPanel result={result} onShare={onShare} />
    </div>
  );
};

export default SerpChecker;
